import { defineComponent as _defineComponent } from 'vue'
import { ActionProps } from '@/components/atoms/action/Action.vue';
import FormContainer from '@/components/molecules/form/FormContainer.vue';
import PageHeader from '@/components/molecules/page/header/PageHeader.vue';
import RequestService from '@/services/api/RequestService';
import { formatNumberToMoneyString, parseMoneyToNumber } from '@/utils/formatters';
import { computed, getCurrentInstance, onMounted, reactive } from 'vue';
import { NumberInputData, TextInputData } from '@/components/molecules/input/types';
import Status from '@/enum/Status';

type QuoteRequestForm = {
    business_interruption: TextInputData;
    building: TextInputData;
    content: TextInputData;
    total_sum_secured: TextInputData;
    original_gross_bonus: TextInputData;
    original_tax: TextInputData;
    new_tax: NumberInputData;
    tax_variation: TextInputData;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'QuoteRequest',
  setup(__props) {

const instance = getCurrentInstance();
const proxy = instance && instance.proxy as any;

const isLoading = reactive({ value: false });
const requestService = new RequestService(proxy);
const status = reactive({ value: 0 });

const moneyFields: string[] = [
    'original_gross_bonus',
    'original_tax',
    'new_tax',
    'business_interruption',
    'content',
    'building',
    'total_sum_secured',
];

const form = reactive<QuoteRequestForm>({
    building: {
        value: '',
        label: 'Edificio',
        error: false,
        errorMessage: '',
        isMoney: true,
        col: 3
    },
    business_interruption: {
        value: '',
        label: 'Interrupción de negocio',
        error: false,
        errorMessage: '',
        isMoney: true,
        col: 3
    },
    content: {
        value: '',
        label: 'Contenido',
        error: false,
        errorMessage: '',
        isMoney: true,
        col: 3
    },
    total_sum_secured: {
        value: '',
        label: 'Suma Total Asegurada',
        error: false,
        errorMessage: '',
        tooltip: 'Suma de interrupción de negocio, edificio y contenido.',
        readonly: true,
        isMoney: true,
        col: 3
    },
    new_tax: {
        label: 'Tasa',
        append: "%",
        type: "number",
        required: true,
        col: 3
    },
    original_tax: {
        label: 'Tasa preaprobada',
        type: "text",
        required: true,
        readonly: true,
        append: "%",
        value: 0,
        col: 3,
        show: () => status.value === Status.Preaprobado
    },
    tax_variation: {
        label: 'Variación de Tasa',
        type: "text",
        append: "%",
        readonly: true,
        tooltip: 'Variación entre la tasa preaprobada y la tasa actual.',
        value: 0,
        col: 3,
        show: () => status.value === Status.Preaprobado
    },
    original_gross_bonus: {
        label: 'Prima Bruta Original Anual',
        tooltip: 'El resultado de la tasa sobre la suma total asegurada.',
        readonly: true,
        isMoney: true,
        value: '',
        col: 3
    },
});

const formFields = computed(() => {
    const fieldsToReturn = {};

    Object.keys(form).forEach((key: string) => {
        if (form[key].show && !form[key].show()) return;
        fieldsToReturn[key] = form[key];
    });

    return fieldsToReturn;
});

const getTitle = () => {
    const requestId = proxy.$route.params.id;
    return `Cotización de solicitud #${requestId ? requestId : ''}`;
};

const getActions = (): ActionProps[] => {
    return [
        {
            label: 'Cotizar',
            onClick: () => requestService.quoteRequest(proxy.$route.params.id, getPayload()),
            variant: "danger"
        }
    ];
};

const parseMoneyFields = (obj: any) => {
    moneyFields.forEach((key) => {
        if (obj[key]) obj[key] = parseMoneyToNumber(obj[key]);
    });
};

const calculateOriginalGrossBonusFromTax = () => {
    let newTaxValue = form.new_tax && form.new_tax.value ? +form.new_tax.value : 0;
    let totalSumSecuredValue = form.total_sum_secured && form.total_sum_secured.value ? form.total_sum_secured.value : 0;
    const totalSumSecuredNumber = !isNaN(totalSumSecuredValue as number) ? parseFloat(totalSumSecuredValue as string) : parseMoneyToNumber(totalSumSecuredValue as string);
    if (isNaN(newTaxValue) || isNaN(totalSumSecuredNumber)) return;
    const originalGrossBonusValue = totalSumSecuredNumber * (newTaxValue / 100);
    form.original_gross_bonus.value = formatNumberToMoneyString(originalGrossBonusValue);
};

const calculateTaxVariation = () => {
    let newTaxValue = form.new_tax && form.new_tax.value ? +form.new_tax.value : 0;
    let originalTaxValue = form.original_tax && form.original_tax.value ? +form.original_tax.value : 0;
    if (isNaN(newTaxValue) || isNaN(originalTaxValue) || originalTaxValue === 0) return;
    const variation = (newTaxValue / originalTaxValue) - 1;
    form.tax_variation.value = +(variation * 100).toFixed(2);
};

const calculateTotalSumSecured = () => {
    const fieldsToSum = ['business_interruption', 'building', 'content'];
    const total = fieldsToSum.reduce((acc, field) => acc + parseMoneyToNumber(form[field].value), 0);
    form.total_sum_secured.value = formatNumberToMoneyString(total);
};

const clearFieldErrors = () => {
    moneyFields.forEach((key) => {
        if (form[key]) {
            form[key].error = false;
            form[key].errorMessage = '';
        }
    });
};

const setFieldErrors = (errors: Array<{ field: string; message: string }>) => {
    errors.forEach((err) => {
        if (err.field && form[err.field]) {
            form[err.field].error = true;
            form[err.field].errorMessage = err.message || 'Campo inválido';
        }
    });
};

const handleBlur = async () => {
    clearFieldErrors();
    const requestData: any = getRequestData();

    if (requestData.status === false && requestData.data && Array.isArray(requestData.data.errors)) {
        setFieldErrors(requestData.data.errors);
        return;
    }

    calculateOriginalGrossBonusFromTax();
    calculateTaxVariation();
    calculateTotalSumSecured();

    if (requestData.error) {
        proxy.$root.notify(requestData.error, 'warn');
    }
};

const loadRequest = async () => {
    const requestId = proxy.$route.params.id;
    if (!requestId) return;

    try {
        const response: any = await proxy.$root._post(`requests/one/${requestId}`, {
            models: 'companies,branches,control_zones,subscriptors,currencies',
        });

        const request: any = response.data[0] || {};
        const details: any = (request.requests_details && request.requests_details[0]) || {};
        status.value = request.status;
        populateRequestData(details, request);
    } catch (e: any) {
        console.error(e);
        proxy.$root.notify('No se pudo cargar los datos de la solicitud.', 'warn');
    }
};

const populateRequestData = (details: any, request: any) => {
    const requestValues = {
        ...details,
        ...request,
    };

    Object.keys(form).forEach((key) => {
        if (key === 'new_tax') {
            form.new_tax.value = requestValues.original_tax;
            return;
        }
        if (requestValues[key] === undefined) return;
        if (form[key] && typeof form[key] === 'object' && 'value' in form[key]) {
            form[key].value = requestValues[key];
        } else {
            form[key] = requestValues[key];
        }
    });

    calculateOriginalGrossBonusFromTax();
    calculateTaxVariation();
};

const getRequestData = () => ({
    data: {
        total_sum_secured: form.total_sum_secured.value,
        business_interruption: form.business_interruption.value,
        building: form.building.value,
        content: form.content.value,
        original_tax: form.new_tax.value,
        new_tax: form.new_tax.value,
        original_gross_bonus: form.original_gross_bonus.value,
    }
});

const getPayload = () => {
    const { data } = getRequestData();

    return {
        ...data,
        building: parseMoneyToNumber(data.building as string),
        content: parseMoneyToNumber(data.content as string),
        business_interruption: parseMoneyToNumber(data.business_interruption as string),
        total_sum_secured: parseMoneyToNumber(data.total_sum_secured as string),
        original_gross_bonus: parseMoneyToNumber(data.original_gross_bonus as string),
    };
};

const validate = async (values: any, calculate_tax_first: boolean) => {
    const requestToValidate: any = {
        companyId: proxy.$store.state.companyId,
        building: values.building,
        original_gross_bonus: values.original_gross_bonus,
        original_tax: values.original_tax,
        new_tax: values.new_tax,
        content: values.content,
        business_interruption: values.business_interruption,
        total_sum_secured: values.total_sum_secured,
        calculate_tax_first: calculate_tax_first ? 1 : 0,
    };

    parseMoneyFields(requestToValidate);
};

onMounted(async () => {
    isLoading.value = true;
    await loadRequest();
    isLoading.value = false;
});

return { __sfc: true,instance, proxy, isLoading, requestService, status, moneyFields, form, formFields, getTitle, getActions, parseMoneyFields, calculateOriginalGrossBonusFromTax, calculateTaxVariation, calculateTotalSumSecured, clearFieldErrors, setFieldErrors, handleBlur, loadRequest, populateRequestData, getRequestData, getPayload, validate, FormContainer, PageHeader }
}

})